import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  NewCalloutScreen,
  type NewCalloutInitialValues,
} from '@/screens/NewCalloutScreen'
import type { SessionSubtype } from '@/lib/schemas/db'
import { DayBookingsPreview } from '@/components/DayBookingsPreview'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'
import { fromZaLocalToUtcIso, toZaNowDateKey } from '@/lib/datetime'
import { withSpan } from '@/lib/sentry'
import { useDb } from '@/lib/dbContext'
import { createSessionActivityAdapter } from '@/activity/adapters'

interface NewSessionModalContextValue {
  openNewSessionModal: (initialDay?: string, subtype?: SessionSubtype) => void
}

const NewSessionModalContext = createContext<NewSessionModalContextValue | null>(null)

export function useNewSessionModal() {
  const ctx = useContext(NewSessionModalContext)
  if (!ctx) {
    throw new Error('useNewSessionModal must be used within NewSessionModalProvider')
  }
  return ctx
}

const sessionSaveErrorMessage = 'Unable to save session. Please try again.'

const calloutFieldLabels: Record<string, string> = {
  external_id: 'External ID',
  full_name: 'Full name',
  customer_id: 'Customer',
  start_time: 'Start time',
  end_time: 'End time',
  is_whole_day: 'Whole day',
  status: 'Status',
  issue_summary: 'Issue summary',
  contact_address: 'Address',
  contact_name: 'Contact name',
  contact_phone: 'Contact phone',
  contact_email: 'Contact email',
  invoice_name: 'Invoice to',
  invoice_address: 'Invoice address',
  invoice_vat: 'Invoice VAT',
  invoice_email: 'Invoice email',
  invoice_phone: 'Invoice phone',
  invoice_id: 'Invoice ID',
  external_customer_id: 'External customer ID',
}

function toTimestampsFromFormValues(
  values: NewCalloutInitialValues,
): { startTime: string; endTime: string; wholeDay: boolean } {
  const wholeDay = values.wholeDay ?? false
  const day = values.day
  const startTimePart = values.time && !wholeDay ? values.time : '09:00'
  const startTime = fromZaLocalToUtcIso(day, startTimePart)

  let endTime: string
  if (values.endTime && !wholeDay) {
    endTime = fromZaLocalToUtcIso(day, values.endTime)
  } else if (wholeDay) {
    endTime = fromZaLocalToUtcIso(day, '23:59')
  } else {
    const [hourStr, minuteStr] = startTimePart.split(':')
    const hour = Number(hourStr ?? '9')
    const endHour = String(Math.min(23, hour + 1)).padStart(2, '0')
    endTime = fromZaLocalToUtcIso(day, `${endHour}:${minuteStr ?? '00'}`)
  }

  return { startTime, endTime, wholeDay }
}

export function NewSessionModalProvider({ children }: { children: ReactNode }) {
  const db = useDb()
  const remoteActivity = useMemo(() => createSessionActivityAdapter(db), [db])
  const [isOpen, setIsOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [initialDayOverride, setInitialDayOverride] = useState<string | null>(null)
  const [sessionSubtype, setSessionSubtype] = useState<SessionSubtype>('Sit-down Session')

  const openNewSessionModal = useCallback((initialDay?: string, subtype?: SessionSubtype) => {
    setErrorMessage(null)
    setInitialDayOverride(initialDay ?? null)
    setSessionSubtype(subtype ?? 'Sit-down Session')
    setIsOpen(true)
  }, [])

  const handleSave = useCallback(
    async (values: NewCalloutInitialValues) => {
      setErrorMessage(null)
      try {
        await withSpan(
          {
            op: 'callout.create',
            name: 'Create session from global modal',
          },
          async () => {
            const { startTime, endTime, wholeDay } = toTimestampsFromFormValues(values)

            let customerId = values.customerId

            if (!customerId && values.customerName.trim().length > 0) {
              const customer = await withSpan(
                {
                  op: 'customer.create',
                  name: 'Create customer during callout booking',
                },
                () =>
                  db.createCustomerAndWait({
                    fullName: values.customerName,
                    externalId: '',
                    contactAddress: values.address,
                    contactEmail: values.email,
                    contactPhone: values.phone,
                    invoiceName: values.invoiceTo || values.customerName,
                    invoiceAddress: values.invoiceAddress ?? values.address,
                    invoiceEmail: values.invoiceEmail ?? values.email,
                    invoicePhone: values.phone,
                    invoiceVat: values.vat ?? '',
                  }),
              )
              customerId = customer.id
            }

            const callout = await db.createSessionAndWait({
              customerId: customerId ?? '',
              startTime,
              endTime,
              isWholeDay: wholeDay,
              status: 'Booked',
              sessionSubtype,
              issueSummary:
                values.issueSummary?.trim() ||
                values.notes?.trim() ||
                'TeamViewer Session',
              contactAddress: values.address,
              contactName: values.customerName,
              contactPhone: values.phone,
              contactEmail: values.email,
              invoiceName: values.invoiceTo || values.customerName,
              invoiceVat: values.vat ?? '',
              invoiceAddress: values.invoiceAddress ?? values.address,
              invoiceEmail: values.invoiceEmail ?? values.email,
              invoicePhone: values.phone,
              invoiceId: undefined,
              billablesConsumed: [],
              auditLogs: [],
              statusChanges: [],
            })

            if (values.notes && values.notes.trim().length > 0) {
              remoteActivity.appendLog(callout.id, {
                text: values.notes,
              })
            }
          },
        )

        setIsOpen(false)
        setInitialDayOverride(null)
      } catch (error) {
        setErrorMessage(
          formatPocketBaseError(error, {
            fallbackMessage: sessionSaveErrorMessage,
            fieldLabels: calloutFieldLabels,
          }),
        )
      }
    },
    [db, sessionSubtype],
  )

  const initialValues: NewCalloutInitialValues = {
    day: initialDayOverride ?? toZaNowDateKey(),
    wholeDay: false,
    customerName: '',
    address: '',
    phone: '',
    email: '',
    invoiceTo: '',
    issueSummary: '',
  }

  const value = useMemo(
    () => ({ openNewSessionModal }),
    [openNewSessionModal],
  )

  return (
    <NewSessionModalContext.Provider value={value}>
      {children}

      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            const shouldClose = window.confirm(
              'Discard this draft session? Unsaved changes will be lost.',
            )
            if (!shouldClose) return
            setErrorMessage(null)
            setInitialDayOverride(null)
            setSessionSubtype('Sit-down Session')
          }

          setIsOpen(open)
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>New session</DialogTitle>
            <DialogDescription>
              Book a TeamViewer or sit-down session.
            </DialogDescription>
          </DialogHeader>
          <NewCalloutScreen
            showHeader={false}
            mode="create"
            initialValues={initialValues}
            errorMessage={errorMessage}
            renderDayBookings={(booking) => <DayBookingsPreview {...booking} />}
            bookingExtra={(
              <div>
                <p className="mb-1 text-[11px] text-slate-500">Session type</p>
                <input type="hidden" name="sessionSubtype" value={sessionSubtype} />
                <div className="inline-grid w-fit grid-cols-2 rounded-full border border-slate-300 bg-white p-0.5">
                  <button
                    type="button"
                    className={
                      sessionSubtype === 'TeamViewer Session'
                        ? 'cursor-pointer rounded-full bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white'
                        : 'cursor-pointer rounded-full px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100'
                    }
                    title="Remote support session"
                    onClick={() => setSessionSubtype('TeamViewer Session')}
                  >
                    TeamViewer
                  </button>
                  <button
                    type="button"
                    className={
                      sessionSubtype === 'Sit-down Session'
                        ? 'cursor-pointer rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white'
                        : 'cursor-pointer rounded-full px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100'
                    }
                    title="In-person at the office or customer"
                    onClick={() => setSessionSubtype('Sit-down Session')}
                  >
                    Sit-down
                  </button>
                </div>
              </div>
            )}
            onSave={handleSave}
            onCancel={() => {
              setIsOpen(false)
              setErrorMessage(null)
              setInitialDayOverride(null)
              setSessionSubtype('Sit-down Session')
            }}
          />
        </DialogContent>
      </Dialog>
    </NewSessionModalContext.Provider>
  )
}
